<template>
  <div class="col-12" style="padding-top: 3em">
    <div class="container">
      <div class="row row-grid align-items-center">
        <div class="col-12">
          <country-flag
            :country="getCityData.iso3"
            size="big"
            class="pull-left mr-2"
          />
          <h2>
            {{ getCityData.city }}
            <small class="text-muted">{{ getCityData.country }}</small>
          </h2>
          <hr style="margin-top: 1em; margin-bottom: 1em" />
        </div>
      </div>

      <div class="row row-grid align-items-center mt-0">
        <div class="col-md-6 order-md-2 text-center">
          <RadarComparison
            :chartData="this.chartData"
            :showLegend="true"
          ></RadarComparison>
        </div>
        <div class="col-md-6 order-md-1">
          <div class="bg-lightgrey country-container">
            <div style="z-index: 1000">
              <ul class="list-unstyled mt-2">
                <li class="py-2">
                  <div class="d-flex align-items-center country-stat">
                    <div>
                      <p class="stat-header font-weight-600 text-uppercase">
                        CFRI Rank
                      </p>
                      <h3 class="mb-0">
                        {{ getCityData.future_readiness_rank }} of 102
                      </h3>
                    </div>
                  </div>
                  <hr class="w-100 mt-3 mb-1 mx-0" />
                </li>
                <li class="py-2">
                  <div class="d-flex align-items-center country-stat">
                    <div>
                      <p class="stat-header font-weight-600 text-uppercase">
                        CFRI Score
                      </p>
                      <h6 class="mb-0">
                        {{
                          parseFloat(
                            getCityData.future_readiness_score
                          ).toFixed(2)
                        }}
                      </h6>
                    </div>
                  </div>
                </li>
                <li class="py-2">
                  <div class="d-flex align-items-center country-stat">
                    <div>
                      <p class="stat-header font-weight-600 text-uppercase">
                        Income Group
                      </p>
                      <h6 class="mb-0">{{ getCityData.income_group }}</h6>
                    </div>
                  </div>
                </li>
                <li class="py-2">
                  <div class="d-flex align-items-center country-stat">
                    <div>
                      <p class="stat-header font-weight-600 text-uppercase">
                        Regional Group
                      </p>
                      <h6 class="mb-0">{{ getCityData.region }}</h6>
                    </div>
                  </div>
                </li>
                <li class="py-2">
                  <div class="d-flex align-items-center country-stat">
                    <div>
                      <p class="stat-header font-weight-600 text-uppercase">
                        Population (millions)
                      </p>
                      <h6 class="mb-0">
                        {{
                          parseFloat(getCityData.population / 1000000).toFixed(
                            2
                          )
                        }}
                      </h6>
                    </div>
                  </div>
                </li>
                <li class="py-2">
                  <div class="d-flex align-items-center country-stat">
                    <div>
                      <p class="stat-header font-weight-600 text-uppercase">
                        GDP Per Capita (US$)
                      </p>
                      <h6 class="mb-0">
                        {{
                          parseFloat(getCityData.gdp_per_capita)
                            .toFixed(2)
                            .toLocaleString()
                        }}
                      </h6>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div class="row row-grid align-items-center">
        <div class="col-12 mt-5 p-0">
          <h4 class="text-center">Rank by pillar and sub-pillar in the CFRI</h4>
          <div class="text-center my-3" style="text-align: center; width: 100%">
            <p
              class="font-weight-500 help-text-green text-center"
              style="text-align: center"
            >
              Please scroll to the right to see additional pillars
            </p>
          </div>
          <CityRankings
            :city-rankings="this.cityRanks"
            :selected-city="this.selectedCity"
          ></CityRankings>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RadarComparison from "../../components-2025/charts/RadarComparison";
import citiesData from "../../data_2025/cities.json";
import CityRankings from "./CityRankings";

export default {
  name: "CityProfile",
  components: {
    CityRankings,
    RadarComparison,
  },
  props: {
    selectedCity: {
      type: String,
      default: "san-francisco",
    },
  },
  data() {
    return {
      citiesData: citiesData,
      visible: false,
    };
  },
  computed: {
    getCityData() {
      return (
        this.citiesData[this.selectedCity] || this.citiesData["san-francisco"]
      );
    },
    cityRanks() {
      // Convert city data to rankings format
      const city = this.getCityData;
      return {
        [this.selectedCity]: {
          // Main pillars
          people_centricity: {
            rank: city.people_centricity_rank,
            score: city.people_centricity_score,
          },
          openness: { rank: city.openness_rank, score: city.openness_score },
          sustainability: {
            rank: city.sustainability_rank,
            score: city.sustainability_score,
          },
          institutions_governance: {
            rank: city.institutions_governance_rank,
            score: city.institutions_governance_score,
          },
          transport_infrastructure: {
            rank: city.transport_infrastructure_rank,
            score: city.transport_infrastructure_score,
          },
          innovation: {
            rank: city.innovation_rank,
            score: city.innovation_score,
          },
          vibrancy: { rank: city.vibrancy_rank, score: city.vibrancy_score },
          e_intelligence: {
            rank: city.e_intelligence_rank,
            score: city.e_intelligence_score,
          },

          // Sub-pillars
          liveability: {
            rank: city.liveability_rank,
            score: city.liveability_score,
          },
          access_to_services: {
            rank: city.access_to_services_rank,
            score: city.access_to_services_score,
          },
          investment_openness: {
            rank: city.investment_openness_rank,
            score: city.investment_openness_score,
          },
          talent_competitiveness: {
            rank: city.talent_competitiveness_rank,
            score: city.talent_competitiveness_score,
          },
          urban_design: {
            rank: city.urban_design_rank,
            score: city.urban_design_score,
          },
          energy_waste: {
            rank: city.energy_waste_rank,
            score: city.energy_waste_score,
          },
          institutions: {
            rank: city.institutions_rank,
            score: city.institutions_score,
          },
          governance: {
            rank: city.governance_rank,
            score: city.governance_score,
          },
          mobility: { rank: city.mobility_rank, score: city.mobility_score },
          digital_infrastructure: {
            rank: city.digital_infrastructure_rank,
            score: city.digital_infrastructure_score,
          },
          rd_intensity: {
            rank: city.rd_intensity_rank,
            score: city.rd_intensity_score,
          },
          ip_intensity: {
            rank: city.ip_intensity_rank,
            score: city.ip_intensity_score,
          },
          financial_activity: {
            rank: city.financial_activity_rank,
            score: city.financial_activity_score,
          },
          cultural_vibrancy: {
            rank: city.cultural_vibrancy_rank,
            score: city.cultural_vibrancy_score,
          },
          data_readiness: {
            rank: city.data_readiness_rank,
            score: city.data_readiness_score,
          },
          ai_readiness: {
            rank: city.ai_readiness_rank,
            score: city.ai_readiness_score,
          },
        },
      };
    },
    chartData() {
      const city = this.getCityData;
      const labels = [
        "People Centricity",
        "Openness",
        "Sustainability",
        "Institutions & Governance",
        "Transport & Infrastructure",
        "Innovation",
        "Vibrancy",
        "e-Intelligence",
      ];

      const chartDataSet = {
        labels: labels,
        datasets: [
          {
            label: city.city,
            data: [
              city.people_centricity_score,
              city.openness_score,
              city.sustainability_score,
              city.institutions_governance_score,
              city.transport_infrastructure_score,
              city.innovation_score,
              city.vibrancy_score,
              city.e_intelligence_score,
            ],
            backgroundColor: "rgba(54, 162, 235, 0.2)",
            borderColor: "rgba(54, 162, 235, 1)",
            borderWidth: 2,
          },
        ],
      };
      return chartDataSet;
    },
  },
};
</script>

<style>
.country-container {
  padding: 1em;
  border-radius: 0.25rem;
}

.country-stat {
  min-height: 50px;
}

.stat-header {
  margin-bottom: 0px;
  font-size: small;
  font-weight: 700 !important;
  color: #4a4a4a !important;
}
</style>
